import { TicketTimeData } from '@beat-venues/common-types';
import { dateFormatter } from '@beat-venues/utils';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import { useTranslation } from 'next-i18next';
import { type ReactNode, Dispatch, SetStateAction, type JSX } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import {
    BellIcon,
    Button,
    ButtonOrLinkProps,
    Heading,
    Text,
} from '../../atoms';
import type { CommonProps } from '../../types';
import { Modal } from '../modal';

export interface ReminderButtonProps
    extends CommonProps,
        ButtonOrLinkProps,
        VariantProps<typeof ReminderButtonStyles> {
    children?: ReactNode;
    eventData: {
        title: string;
        label: string;
        icalLabel: string;
    };
    location?: string;
    reminderDate?: string | null;
    variant: 'small' | 'custom';
    modalIsShown: boolean;
    setModalIsShown: Dispatch<SetStateAction<boolean>>;
}

export const ReminderButtonStyles = tv({
    slots: {
        wrapper: '',
        reminderButton:
            'group absolute right-14 translate-x-14 whitespace-nowrap rounded-l-none pl-12 text-right hover:translate-x-full focus:translate-x-full ',
        reminderLabel:
            'font-medium opacity-0 delay-0 group-hover:opacity-100 group-hover:delay-200 group-focus:opacity-100 group-focus:delay-200',
        reminderIcon:
            'text-buttonPrimary pointer-events-none absolute left-2 top-2.5 h-5 w-5',
    },
    variants: {
        variant: {
            small: { wrapper: 'relative w-10' },
            custom: {},
        },
    },
});

export const ReminderButton = ({
    className,
    children,
    eventData,
    location,
    reminderDate,
    variant,
    modalIsShown,
    setModalIsShown,
    testId = 'reminder-button',
    id,
    ...props
}: ReminderButtonProps): JSX.Element | undefined => {
    const { wrapper, reminderButton, reminderLabel, reminderIcon } =
        ReminderButtonStyles({ variant });

    const { t } = useTranslation(['event']);

    if (!reminderDate) {
        return;
    }

    const untilDate = new Date(
        new Date(reminderDate).setHours(new Date(reminderDate).getHours() + 1)
    ).toISOString();

    const renderButton = () => {
        if (variant === 'custom') {
            return children;
        }
        return (
            <>
                <Button
                    aria-expanded={modalIsShown}
                    className={reminderButton()}
                    onClick={() => setModalIsShown(true)}
                    {...(id ? { id } : undefined)}
                >
                    <Text className={reminderLabel()}>
                        {t(`reminder.cta.setReminder`, {
                            ns: 'event',
                        })}
                    </Text>
                </Button>
                <BellIcon className={reminderIcon()} size="md" />
            </>
        );
    };

    return (
        <div className={wrapper({ className })} {...props} data-testid={testId}>
            {renderButton()}
            <Modal
                isShown={modalIsShown}
                size="fit-content"
                onClose={() => setModalIsShown(false)}
            >
                <Modal.Header onClose={() => setModalIsShown(false)}>
                    <Heading as="h4" className="mb-0">
                        {eventData.label}
                    </Heading>
                </Modal.Header>
                <Modal.Content>
                    <div className="overflow-hidden rounded">
                        <AddToCalendarButton
                            buttonStyle="none"
                            buttonsList={true}
                            customLabels={{
                                ical: eventData.icalLabel,
                            }}
                            endDate={dateFormatter.formatIcalDateTime(
                                untilDate,
                                'date'
                            )}
                            endTime={dateFormatter.formatIcalDateTime(
                                untilDate,
                                'time'
                            )}
                            hideCheckmark={true}
                            location={location}
                            name={`${eventData.title} ticket reminder`}
                            options={['Apple', 'Google', 'iCal', 'Outlook.com']}
                            startDate={dateFormatter.formatIcalDateTime(
                                reminderDate,
                                'date'
                            )}
                            startTime={dateFormatter.formatIcalDateTime(
                                reminderDate,
                                'time'
                            )}
                            timeZone="Europe/Brussels"
                        ></AddToCalendarButton>
                    </div>
                </Modal.Content>
            </Modal>
        </div>
    );
};
